import React from 'react';

import { Trans } from '@lyearn/i18n';
import { TFunction } from '@lyearn/i18n';
import { ErrorPosition, Fields, FieldType, SectionLayout } from '@/components/Form';
import { ScorecardQuestionType, SkillType } from '@/types/schema';

import Questions from '../components/Questions';
import { FormSection } from '../interfaces';
import { getDefaultQuestionItem, isAnyQuestionTextEmpty, isQuestionsEmpty } from '../utils';

export type ScorecardCreatorFormValues = {
  title: string;
  skillId?: string | null;
  questions?: Omit<ScorecardQuestionType, 'id'>[] | null;
};

export const getInitialValues = (): ScorecardCreatorFormValues => ({
  title: '',
  skillId: null,
  questions: [getDefaultQuestionItem(1)],
});

const adaptSkills = (skills?: Pick<SkillType, 'id' | 'name'>[]) => {
  if (!skills?.length) {
    return [];
  }

  return skills.map((skill) => ({ label: skill.name, value: skill.id }));
};

export const getFormFields = (
  t: TFunction,
  payload: { skills?: Pick<SkillType, 'id' | 'name'>[] },
): Fields<ScorecardCreatorFormValues> => {
  const { skills } = payload;

  return {
    title: {
      name: 'title',
      placeholder: t('Scorecard:TitlePlaceholder'),
      label: t('Common:Form.Title'),
      type: FieldType.Text,
      size: 'large',
    },
    skillId: {
      name: 'skillId',
      label: t('Common:SkillDetails.Skill'),
      required: false,
      type: FieldType.Picklist,
      placeholder: t('Scorecard:SkillPlaceholder'),
      size: 'large',
      picklistValues: adaptSkills(skills),
    },
    questions: {
      name: 'questions',
      type: FieldType.Custom,
      errorPosition: ErrorPosition.Custom,
      Component: ({ value, setFieldValue, setErrorElement, touched, error }) => {
        const fieldKey = 'questions';
        const onChange = (nextValue: any) => {
          setFieldValue(fieldKey, [...nextValue], true);
        };

        const showError = touched && error;

        return (
          <Questions
            error={showError ? error : undefined}
            questions={value as ScorecardQuestionType[]}
            setErrorElement={setErrorElement}
            onChange={onChange}
          />
        );
      },
    },
  };
};

export const getSectionLayout = (t: TFunction) =>
  [
    {
      id: FormSection.Basic,
      layout: ['title', 'skillId'],
    },
    {
      id: FormSection.Questions,
      name: t('Scorecard:Questions'),
      description: t('Scorecard:QuestionsDescription'),
      layout: ['questions'],
    },
  ] as SectionLayout;

export const validateForm = (values: ScorecardCreatorFormValues) => {
  const errors: any = {};
  if (!values.title) {
    errors.title = <Trans i18nKey="Scorecard:TitleError" />;
  }
  if (isQuestionsEmpty(values.questions)) {
    errors.questions = <Trans i18nKey="Scorecard:QuestionsError" />;
  } else if (isAnyQuestionTextEmpty(values.questions!)) {
    errors.questions = <Trans i18nKey="Scorecard:QuestionCantBeEmpty" />;
  }

  return errors;
};
